import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import {
  Box,
  Badge,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaArrowRight,
  FaCheckCircle,
  FaBolt,
  FaShieldAlt,
} from 'react-icons/fa';
import { APP_NAME, APP_DESCRIPTION } from '../config/branding';
import LandingNavbar from '../components/landing/LandingNavbar';
import LandingFooter from '../components/landing/LandingFooter';
import FeaturesSection from '../components/landing/FeaturesSection';
import PricingSection from '../components/PricingSection';
import { LANDING_IMAGES } from '../constants/landingAssets';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

const stats = [
  { value: '14 days', label: 'Pro trial included' },
  { value: '3 plans', label: 'Scale as you grow' },
  { value: '1 workspace', label: 'Clients, projects & tasks' },
  { value: '24/7', label: 'Cloud access anywhere' },
];

const steps = [
  {
    step: '01',
    title: 'Create your workspace',
    desc: 'Sign up in minutes and invite your team. Every new account starts with a 14-day Pro trial.',
  },
  {
    step: '02',
    title: 'Organize clients & projects',
    desc: 'Structure delivery around real client work with customizable types, statuses, and priorities.',
  },
  {
    step: '03',
    title: 'Ship with clarity',
    desc: 'Track tasks, collaborate in context, and upgrade only when your team needs more power.',
  },
];

const highlights = [
  'Multi-tenant workspaces for agencies',
  'Customizable project & task settings',
  'Stripe-powered billing & seat management',
  'Role-based access for admins, editors & viewers',
];

const LandingPage = () => {
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const sectionBg = useColorModeValue('white', 'gray.800');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const accentSoft = useColorModeValue('blue.50', 'whiteAlpha.100');

  useEffect(() => {
    if (window.location.hash === '#pricing') {
      scrollToSection('pricing');
    }
  }, []);

  return (
    <Box bg={pageBg} minH="100vh" overflowX="hidden">
      <Box
        position="relative"
        bgImage={`linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(49, 46, 129, 0.82) 55%, rgba(30, 64, 175, 0.78) 100%), url(${LANDING_IMAGES.hero})`}
        bgSize="cover"
        bgPosition="center"
        color="white"
      >
        <LandingNavbar onScrollTo={scrollToSection} />

        <Container maxW="7xl" pt={{ base: 10, md: 20 }} pb={{ base: 16, md: 24 }}>
          <Grid templateColumns={{ base: '1fr', lg: '1.1fr 0.9fr' }} gap={{ base: 12, lg: 16 }} alignItems="center">
            <VStack align="start" spacing={8} animation={`${fadeUp} 0.8s ease-out`}>
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                bg="whiteAlpha.200"
                color="white"
                fontSize="sm"
                textTransform="none"
              >
                Built for software agencies
              </Badge>

              <Heading
                size="2xl"
                lineHeight="1.1"
                fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
                fontWeight="extrabold"
              >
                Deliver client work with clarity, speed, and control
              </Heading>

              <Text fontSize={{ base: 'lg', md: 'xl' }} color="whiteAlpha.900" maxW="2xl" lineHeight="tall">
                {APP_DESCRIPTION}
                Start free, then scale with plans designed for growing agencies.
              </Text>

              <Stack direction={{ base: 'column', sm: 'row' }} spacing={4} w={{ base: 'full', sm: 'auto' }}>
                <Button
                  as={RouterLink}
                  to="/auth"
                  size="lg"
                  rightIcon={<FaArrowRight />}
                  bgGradient="linear(to-r, blue.400, purple.400)"
                  _hover={{ bgGradient: 'linear(to-r, blue.500, purple.500)', transform: 'translateY(-2px)' }}
                  color="white"
                  px={8}
                  boxShadow="xl"
                >
                  Start free trial
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="whiteAlpha.700"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  onClick={() => scrollToSection('pricing')}
                  px={8}
                >
                  View pricing
                </Button>
              </Stack>

              <HStack spacing={6} flexWrap="wrap" color="whiteAlpha.900" fontSize="sm">
                <HStack><Icon as={FaCheckCircle} color="green.300" /><Text>14-day Pro trial</Text></HStack>
                <HStack><Icon as={FaCheckCircle} color="green.300" /><Text>No credit card required</Text></HStack>
                <HStack><Icon as={FaCheckCircle} color="green.300" /><Text>Cancel anytime</Text></HStack>
              </HStack>
            </VStack>

            <Box position="relative" animation={`${float} 6s ease-in-out infinite`}>
              <Box
                position="absolute"
                inset="-8%"
                bgGradient="radial(circle, blue.400 0%, transparent 70%)"
                opacity={0.35}
                filter="blur(40px)"
              />
              <Box
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="2xl"
                borderWidth="1px"
                borderColor="whiteAlpha.300"
                bg="whiteAlpha.100"
                backdropFilter="blur(8px)"
              >
                <Image
                  src={LANDING_IMAGES.dashboard}
                  alt={`${APP_NAME} dashboard preview`}
                  w="full"
                  h={{ base: '240px', md: '320px', lg: '380px' }}
                  objectFit="cover"
                />
              </Box>

              <Box
                position="absolute"
                bottom={{ base: -4, md: -6 }}
                left={{ base: 4, md: -6 }}
                bg={cardBg}
                color="gray.800"
                px={5}
                py={4}
                borderRadius="xl"
                boxShadow="2xl"
                maxW="220px"
              >
                <HStack spacing={3}>
                  <Flex
                    align="center"
                    justify="center"
                    boxSize={10}
                    borderRadius="lg"
                    bg="green.100"
                    color="green.600"
                  >
                    <Icon as={FaBolt} />
                  </Flex>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Team velocity</Text>
                    <Text fontWeight="bold">+32% this month</Text>
                  </Box>
                </HStack>
              </Box>
            </Box>
          </Grid>
        </Container>
      </Box>

      <Box bg={sectionBg} borderBottomWidth="1px" borderColor={borderColor}>
        <Container maxW="7xl" py={10}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            {stats.map((item) => (
              <VStack key={item.label} spacing={1} textAlign="center">
                <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color="blue.600">
                  {item.value}
                </Text>
                <Text color={muted} fontSize="sm">{item.label}</Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <FeaturesSection />

      <Box bg={accentSoft} py={{ base: 16, md: 24 }}>
        <Container maxW="7xl">
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={12} alignItems="center">
            <Box borderRadius="2xl" overflow="hidden" boxShadow="2xl">
              <Image
                src={LANDING_IMAGES.workspace}
                alt="Modern agency workspace"
                w="full"
                h={{ base: '280px', lg: '420px' }}
                objectFit="cover"
              />
            </Box>

            <VStack align="start" spacing={6}>
              <Badge colorScheme="purple" px={3} py={1} borderRadius="full" textTransform="none">
                How it works
              </Badge>
              <Heading size="xl">Go from signup to shipping in three steps</Heading>
              <VStack align="stretch" spacing={5} w="full">
                {steps.map((item) => (
                  <HStack key={item.step} align="start" spacing={5}>
                    <Flex
                      minW={12}
                      h={12}
                      align="center"
                      justify="center"
                      borderRadius="xl"
                      bg="white"
                      color="blue.600"
                      fontWeight="bold"
                      boxShadow="md"
                    >
                      {item.step}
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={1}>{item.title}</Heading>
                      <Text color={muted}>{item.desc}</Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </Grid>
        </Container>
      </Box>

      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="7xl">
          <Grid templateColumns={{ base: '1fr', lg: '0.9fr 1.1fr' }} gap={12} alignItems="center">
            <VStack align="start" spacing={6}>
              <Badge colorScheme="green" px={3} py={1} borderRadius="full" textTransform="none">
                Why agencies choose us
              </Badge>
              <Heading size="xl">Enterprise-ready foundations, startup-friendly pricing</Heading>
              <Text color={muted} fontSize="lg" lineHeight="tall">
                Whether you are a boutique dev shop or a growing digital agency, {APP_NAME} adapts to how your team actually works.
              </Text>
              <VStack align="start" spacing={3} w="full">
                {highlights.map((item) => (
                  <HStack key={item} align="start">
                    <Icon as={FaShieldAlt} color="green.500" mt={1} />
                    <Text color={muted}>{item}</Text>
                  </HStack>
                ))}
              </VStack>
              <Button
                as={RouterLink}
                to="/auth"
                colorScheme="blue"
                size="lg"
                rightIcon={<FaArrowRight />}
              >
                Create your workspace
              </Button>
            </VStack>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <Image
                  src={LANDING_IMAGES.collaboration}
                  alt="Team collaboration"
                  borderRadius="2xl"
                  h="220px"
                  w="full"
                  objectFit="cover"
                  boxShadow="lg"
                />
              </GridItem>
              <GridItem mt={8}>
                <Image
                  src={LANDING_IMAGES.analytics}
                  alt="Analytics dashboard"
                  borderRadius="2xl"
                  h="220px"
                  w="full"
                  objectFit="cover"
                  boxShadow="lg"
                />
              </GridItem>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box bg={sectionBg}>
        <Container maxW="7xl">
          <PricingSection />
        </Container>
      </Box>

      <Box
        position="relative"
        py={{ base: 20, md: 28 }}
        bgImage={`linear-gradient(135deg, rgba(30, 64, 175, 0.92), rgba(88, 28, 135, 0.9)), url(${LANDING_IMAGES.cta})`}
        bgSize="cover"
        bgPosition="center"
        color="white"
      >
        <Container maxW="4xl" textAlign="center">
          <VStack spacing={8}>
            <Heading size="2xl">Ready to streamline your agency delivery?</Heading>
            <Text fontSize="xl" color="whiteAlpha.900" maxW="2xl">
              Join teams who manage clients, projects, and tasks in one place. Start your free trial today.
            </Text>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
              <Button
                as={RouterLink}
                to="/auth"
                size="lg"
                px={10}
                bg="white"
                color="blue.700"
                _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                rightIcon={<FaArrowRight />}
              >
                Get started free
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="whiteAlpha.700"
                color="white"
                _hover={{ bg: 'whiteAlpha.200' }}
                onClick={() => scrollToSection('pricing')}
                px={10}
              >
                Compare plans
              </Button>
            </Stack>
          </VStack>
        </Container>
      </Box>

      <LandingFooter />
    </Box>
  );
};

export default LandingPage;
